<template>
  <!-- Collapsed sidebar: an icon whose tooltip carries the whole message -->
  <div v-if="visible && collapsed"
       class="flex justify-center py-1.5"
       role="img"
       :aria-label="tooltip"
       :title="tooltip">
    <span class="w-7 h-7 rounded-lg flex items-center justify-center bg-amber-400/15 text-amber-200">
      <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
      </svg>
    </span>
  </div>

  <!-- Expanded sidebar: a pill that opens a short explanation in place -->
  <div v-else-if="visible" class="mb-1.5">
    <button type="button"
            @click="open = !open"
            :aria-expanded="open"
            class="w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-left transition-colors bg-amber-400/15 hover:bg-amber-400/25 text-amber-200">
      <svg class="flex-shrink-0 w-3.5 h-3.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"/>
      </svg>
      <span class="min-w-0 flex-1">
        <span class="block text-[10px] leading-tight text-amber-200/70">{{ t.signed_in_with }}</span>
        <span class="block text-[11px] font-semibold leading-tight truncate" :title="label">{{ label }}</span>
      </span>
      <svg class="flex-shrink-0 w-3 h-3 transition-transform duration-150 text-amber-200/60"
           :class="open ? 'rotate-180' : ''"
           fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true">
        <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
      </svg>
    </button>

    <div v-if="open" class="mt-1.5 px-3 py-2.5 rounded-lg bg-white/[0.06]">
      <p class="text-[11px] leading-relaxed text-white/70">{{ explanation }}</p>
      <button type="button"
              @click="$emit('sign-out')"
              class="mt-2 text-[11px] font-semibold text-amber-200 hover:text-amber-100 transition-colors">
        {{ t.sign_out }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useConstants } from 'composables/useConstants.js'

// How the session signed in, from CURRENT_USER.login_identity
// (app/controllers/concerns/login_identity.rb). The note only appears when the
// session did not sign in with the primary email. Impersonation is left out:
// the sidebar's "Back as Admin" link already says so.
const props = defineProps({
  identity: { type: Object, default: null },
  primaryEmail: { type: String, default: '' },
  collapsed: { type: Boolean, default: false },
})

defineEmits(['sign-out'])

const { I18N, parseLocale } = useConstants()
const t = I18N.secondary_emails.sidebar

const open = ref(false)

const visible = computed(() => ['secondary', 'restricted'].includes(props.identity?.kind))
const label = computed(() => props.identity?.email || t.a_linked_email)
const explanation = computed(() => parseLocale(t.explanation, { email: props.primaryEmail }))
const tooltip = computed(() => `${t.signed_in_with} ${label.value}`)
</script>
